import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { createUser } from './adminPageRequest';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { PrivilegeLevels } from '../../shared/constants/PrivilegeLevels';
import { SingleSelect } from '../Cells/CellTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
function AddUserModal({ users, open, setOpen, editingUserPrivilege, callback }) {
  const defaultValues = {
    username: '',
    name: '',
    email: '',
    privilege: 0,
  };
  const dispatch = useDispatch();

  const schema = yup.object().shape({
    username: yup
      .string()
      .required()
      .matches(/^[a-zA-Z0-9.]+$/, 'Usernames may only include alphanumeric characters or periods'),
    name: yup.string().required(),
    email: yup.string().email().required(),
    privilege: yup.number().required(),
  });

  //get only user, manager, admin
  let privMapping = {};

  const privilegeLevelsForSelect = Object.keys(PrivilegeLevels)
    .map((level) => {
      privMapping[PrivilegeLevels[level].level] = PrivilegeLevels[level].title;
      if (PrivilegeLevels[level].level <= editingUserPrivilege)
        return {
          title: PrivilegeLevels[level].title,
          value: PrivilegeLevels[level].level,
        };
      return null;
    })
    .filter((level) => level !== null);

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const forSubmit = async (data) => {
    if (users.some((user) => user.email === data.email)) {
      setError('email', { type: 'manual', message: 'There is an existing user with this email' });
    } else {
      //create a new user
      createUser(data)
        .then((response) => {
          callback(response.user);
          dispatch(openSnackbar({ severity: 'success', message: 'User successfully created' }));
        })
        .catch((e) => {
          console.log(e.message);
          dispatch(openSnackbar({ severity: 'error', message: 'User failed to be created' }));
        });
      handleClose();
    }
  };

  const queryClient = useQueryClient();
  const newUserMutation = useMutation({
    mutationFn: (data) => forSubmit(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users', 'adminPage'] });
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
    },
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((data) => newUserMutation.mutate(data))}>
        <DialogTitle sx={{ fontFamily: "'Open Sans', sans-serif" }}>Add User</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: '2vh',
              pb: '1vh',
              borderTop: 'solid gray 1px',
              mt: '2vh',
              mb: '1vh',
            }}
          >
            <Controller
              name="username"
              control={control}
              defaultValue={defaultValues.username}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Username"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  error={errors.username}
                  helperText={errors.username && errors.username.message}
                />
              )}
            />
          </Box>
          <Box sx={{ pt: '1vh', pb: '1vh', mt: '1vh', mb: '1vh' }}>
            <Controller
              name="name"
              control={control}
              defaultValue={defaultValues.name}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  error={errors.name}
                  helperText={errors.name && errors.name.message}
                />
              )}
            />
          </Box>
          <Box sx={{ pt: '1vh', pb: '1vh', mt: '1vh', mb: '1vh' }}>
            <Controller
              name="email"
              control={control}
              defaultValue={defaultValues.email}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  error={errors.email}
                  helperText={errors.email && errors.email.message}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              pt: '1vh',
              pb: '2vh',
              borderBottom: 'solid gray 1px',
              mt: '1vh',
              mb: '2vh',
            }}
          >
            <Controller
              name="privilege"
              control={control}
              defaultValue={defaultValues.privilege}
              render={({ field: { onChange, value } }) => (
                <SingleSelect
                  value={value}
                  items={privilegeLevelsForSelect}
                  label={'Account Type'}
                  callBack={(val) => {
                    onChange(val);
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddUserModal;
